import { useEffect, useRef, useState } from "react";
import { useBenefits } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";

export default function BenefitsSection() {
  const { data: benefits, loading, error, reload } = useBenefits();
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-background-100 py-14 md:py-20"
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
            visible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-accent-100 text-accent-700 text-xs md:text-sm font-label font-medium rounded-full mb-4">
            <i className="ri-shining-fill" />
            Lợi ích vượt trội
          </span>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Tại sao chọn Xanh Wellness?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto font-body">
            Trải nghiệm công nghệ cao giúp bạn đạt được sức khỏe và vóc dáng mong muốn một cách an toàn, nhanh chóng.
          </p>
        </div>

        {/* Benefits Grid */}
        <ContentStatus loading={loading} error={error} onRetry={reload}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.id}
              className={`bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-all duration-500 ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{
                transitionDelay: visible ? `${index * 100}ms` : "0ms",
              }}
            >
              <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                <i className={`${benefit.icon} text-xl md:text-2xl`} />
              </div>
              <h3 className="font-display text-base md:text-lg font-semibold text-foreground-950 mb-2">
                {benefit.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed font-body">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
        </ContentStatus>
      </div>
    </section>
  );
}